// Central date formatting helpers.
//
// Display-only (never used for form inputs — those keep native ISO to stay
// compatible with `<input type="date">`).
//
// Costa Rica standard is dd/mm/yyyy. English fallback is mm/dd/yyyy so admins
// using EN locale don't get confused. Both are 4-digit years to avoid century
// ambiguity.

/**
 * Convert an ISO date (YYYY-MM-DD) into the localized dd/mm/yyyy display form.
 * Returns the original input if it's falsy or not parseable.
 *
 * @param {string|null|undefined} iso "YYYY-MM-DD" (may include time; only the date part is used)
 * @param {"es"|"en"} lang default "es"
 */
export function formatDMY(iso, lang = "es") {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return lang === "en" ? `${mo}/${d}/${y}` : `${d}/${mo}/${y}`;
}

/**
 * Same as formatDMY but returns an empty string (not em-dash) when the input
 * is falsy — useful inside sentences ("Fecha de pago: {formatDMYOrEmpty(x)}").
 */
export function formatDMYOrEmpty(iso, lang = "es") {
  if (!iso) return "";
  return formatDMY(iso, lang);
}

/**
 * Día de calendario en hora de COSTA RICA (YYYY-MM-DD).
 *
 * `new Date().toISOString()` da el día en UTC: desde las 6 p.m. de Costa Rica
 * (UTC−6) ya reporta mañana (reporte de la dueña, 14/09/2026: el botón "Hoy"
 * decía 15/09). Tampoco se usa la hora del navegador, porque la escuela opera
 * en Costa Rica pase lo que pase con la computadora de quien esté usando el
 * sistema. TODO "hoy" de la aplicación pasa por acá.
 */
export const SCHOOL_TZ = "America/Costa_Rica";

export function toISODate(d) {
  // en-CA formatea como YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHOOL_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

export function todayISO() {
  return toISODate(new Date());
}

/** Mes actual en Costa Rica (YYYY-MM). */
export function currentMonthISO() {
  return todayISO().slice(0, 7);
}

/** Día de Costa Rica de hace N días (para los botones "Ayer" / "Antier"). */
export function daysAgoISO(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return toISODate(d);
}

/** Hora (hh:mm a. m.) de un instante, siempre en hora de Costa Rica. */
export function crTime(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("es-CR", {
    timeZone: SCHOOL_TZ, hour: "2-digit", minute: "2-digit", hour12: true,
  }).format(new Date(iso));
}

/** Separador de día dentro de un chat: "Hoy", "Ayer" o la fecha completa. */
export function chatDayLabel(iso, lang = "es") {
  if (!iso) return "";
  const day = toISODate(new Date(iso));
  if (day === todayISO()) return lang === "en" ? "Today" : "Hoy";
  if (day === daysAgoISO(1)) return lang === "en" ? "Yesterday" : "Ayer";
  return formatDMY(day, lang);
}

/** Sello corto para la lista de conversaciones: hora si es de hoy, si no la fecha. */
export function chatStamp(iso, lang = "es") {
  if (!iso) return "";
  const day = toISODate(new Date(iso));
  if (day === todayISO()) return crTime(iso);
  if (day === daysAgoISO(1)) return lang === "en" ? "Yesterday" : "Ayer";
  return formatDMY(day, lang);
}

/** Fecha y hora completas de Costa Rica, para los tooltips. */
export function crDateTime(iso, lang = "es") {
  if (!iso) return "";
  return `${formatDMY(toISODate(new Date(iso)), lang)} · ${crTime(iso)}`;
}

/** Solo la fecha (dd/mm/yyyy) de un instante guardado en UTC, en hora de CR. */
export function crDate(iso, lang = "es") {
  if (!iso) return "";
  return formatDMY(toISODate(new Date(iso)), lang);
}

// ---------------------------------------------------------------------------
// Campos <input type="datetime-local">
//
// Un datetime-local NO tiene zona: la persona escribe "01/10/2026 6:05 p.m."
// pensando en hora de Costa Rica. Dos errores que ya se pagaron caros:
//   1. `new Date(valor).toISOString()` interpreta el valor con el reloj del
//      navegador (si la compu está en otra zona, la hora se corre).
//   2. Guardar el ISO en UTC y volverlo a pintar con `.slice(0,16)` muestra la
//      hora en UTC: 6:05 p.m. del 1 se veía como 12:05 a.m. del 2 (+6 h).
// Regla: ENTRADA con crLocalToISO(), SALIDA con isoToCRInput(). Nada más.
// ---------------------------------------------------------------------------
const CR_OFFSET = "-06:00"; // Costa Rica no usa horario de verano

/** "YYYY-MM-DDTHH:mm" escrito en hora de Costa Rica → instante ISO en UTC. */
export function crLocalToISO(local) {
  if (!local) return "";
  const s = String(local).slice(0, 16);
  if (s.length < 16) return "";
  const d = new Date(`${s}:00${CR_OFFSET}`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString(); // tz-ok: el offset de CR va explícito arriba
}

/** Instante ISO (UTC) → "YYYY-MM-DDTHH:mm" en hora de Costa Rica. */
export function isoToCRInput(iso) {
  if (!iso) return "";
  const s = String(iso);
  // Dato viejo sin zona: ya está en hora local, se respeta tal cual.
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) return s.slice(0, 16);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const p = {};
  new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHOOL_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

/** "14:30" → "2:30 p.m.". En 24 h la gente confunde la mañana con la tarde. */
export function hora12(hhmm, lang = "es") {
  const h = parseInt(String(hhmm || "").slice(0, 2), 10);
  const m = String(hhmm || "").slice(3, 5);
  if (Number.isNaN(h)) return hhmm || "";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const suf = lang === "en" ? (h < 12 ? "AM" : "PM") : (h < 12 ? "a.m." : "p.m.");
  return `${h12}:${m} ${suf}`;
}
